import React, { useState, useEffect } from "react";
import Global from "./search/Global";
import Options from "./search/Options";
import Internal from "./search/Internal";
import InvestorContainer from "./search/InvestorContainer";
import axios from "axios";
import { BaseURL } from "../App";

// Recommendation Modal component
const RecommendationModal = ({ isOpen, onClose, onYes }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Modal content - positioned at bottom right */}
      <div className="absolute bottom-8 md:right-8 pointer-events-auto">
        <div className="bg-white p-6 rounded-lg shadow-2xl max-w-md animate-fade-in border border-gray-100">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Do you want me to recommend you something or help on this search?
            </h3>
            <div className="mt-6 flex justify-center gap-4">
              <button
                onClick={onYes}
                className="px-6 py-2 bg-[#fbbb04] text-white rounded-md hover:bg-[#e6ad04] transition-colors"
              >
                YES
              </button>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                NO
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable Pagination component
const Pagination = ({ currentPage, totalPages, btnArray, onPageChange }) => {
  if (totalPages <= 1) return null;

  return (
    <ol className="flex justify-center gap-1 text-xs font-medium my-8">
      {/* First page button */}
      {currentPage > 1 && (
        <li>
          <button
            onClick={() => onPageChange(currentPage - 1)}
            className="inline-flex size-8 items-center justify-center hover:bg-[#e617be] hover:text-white rounded-full border border-gray-100 bg-white text-gray-900"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </li>
      )}

      {/* Page numbers */}
      {btnArray.map((item, idx) => (
        <li key={idx}>
          {item === "..." ? (
            <div className="block size-8 rounded-full border-gray-100 border text-center leading-8 text-primary">
              {item}
            </div>
          ) : parseInt(item) === currentPage ? (
            <div className="block size-8 rounded-full cursor-pointer border-[#fbbb04] bg-[#e617be] text-center leading-8 text-white">
              {item}
            </div>
          ) : (
            <button
              onClick={() => onPageChange(parseInt(item))}
              className="block size-8 hover:bg-[#e617be] cursor-pointer hover:text-white rounded-full border border-pink-500 bg-white text-center leading-8 text-gray-900"
            >
              {item}
            </button>
          )}
        </li>
      ))}

      {/* Last page button */}
      {currentPage < totalPages && (
        <li>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            className="inline-flex size-8 items-center justify-center hover:bg-[#e617be] hover:text-white rounded-full border border-pink-500 bg-white text-gray-900"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </li>
      )}
    </ol>
  );
};

export default function Home() {
  // State management
  const [searchParams, setSearchParams] = useState({
    search: "",
    type: "",
    category: "",
    page: 1,
    type_of: "",
  });
  const [standards, setStandards] = useState([]);
  const [fundingAgencies, setFundingAgencies] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    btnArray: [],
  });

  // Recommendation modal state
  const [isRecommendationModalOpen, setIsRecommendationModalOpen] =
    useState(false);

  // Get URL parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const newParams = { ...searchParams };

    if (queryParams.get("search")) newParams.search = queryParams.get("search");
    if (queryParams.get("type")) newParams.type = queryParams.get("type");
    if (queryParams.get("category"))
      newParams.category = queryParams.get("category");
    if (queryParams.get("page"))
      newParams.page = parseInt(queryParams.get("page")) || 1;
    if (queryParams.get("type_of"))
      newParams.type_of = queryParams.get("type_of");

    setSearchParams(newParams);

    setPagination((prev) => ({
      ...prev,
      currentPage: newParams.page,
    }));

    // If we have search parameters, fetch results
    if (Object.values(newParams).some((value) => value !== "" && value !== 1)) {
      fetchResults(newParams);
    }
  }, [window.location.search]);

  // Handle recommendation modal display timer
  useEffect(() => {
    let timerId;

    // Show recommendation modal 5 seconds after standards search results are displayed
    // Only show on the first page of standards results
    if (
      standards &&
      standards.length > 0 &&
      !loading &&
      searchParams.type === "standards" &&
      searchParams.page === 1
    ) {
      timerId = setTimeout(() => {
        setIsRecommendationModalOpen(true);
      }, 5000); // 5 seconds delay
    }

    // Clean up timer on unmount or when search params change
    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [standards, loading, searchParams.type, searchParams.page]);

  // Handle "YES" button click in recommendation modal
  const handleRecommendationYes = () => {
    setIsRecommendationModalOpen(false);
    // Redirect to the specified website
    // window.location.href = "https://www.gangesventure.com";
    // Redirect to the specified website in a new tab
    window.open(
      process.env.REACT_APP_PUBLIC_TESTING_NAVBAR_ROUTE_URL + "search-table",
      "_blank"
    );
  };

  // Handle search form submission
  const handleSearchSubmit = (params) => {
    // Reset to page 1 when changing search parameters (unless explicitly setting page)
    const newParams = {
      ...searchParams,
      ...params,
      page: params.page || 1, // Reset to page 1 for new searches
    };
    setSearchParams(newParams);

    // Update URL with search parameters
    const url = new URL(window.location);
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        url.searchParams.set(key, value);
      } else {
        url.searchParams.delete(key);
      }
    });
    window.history.pushState({}, "", url);

    // Hide search results text area when changing search parameters
    if (params.type !== "global") {
      setShowSearchResults(false);
    }

    fetchResults(newParams);
  };

  // Handle page change for pagination
  const handlePageChange = (newPage) => {
    if (newPage !== pagination.currentPage) {
      const newParams = { ...searchParams, page: newPage };

      // Update URL
      const url = new URL(window.location);
      url.searchParams.set("page", newPage);
      window.history.pushState({}, "", url);

      // Update state
      setSearchParams(newParams);
      setPagination((prev) => ({
        ...prev,
        currentPage: newPage,
      }));

      // Fetch new results
      fetchResults(newParams);
    }
  };

  // Handle global search results
  const handleGlobalSearchResults = (results) => {
    setSearchResults(results);
    setShowSearchResults(true);
  };

  // Generate pagination buttons array
  const generatePaginationButtons = (current, total) => {
    let buttons = [];

    if (total <= 7) {
      // Show all pages if total is 7 or less
      for (let i = 1; i <= total; i++) {
        buttons.push(i);
      }
    } else {
      // Complex pagination with ellipsis
      if (current <= 3) {
        buttons = [1, 2, 3, 4, 5, "...", total];
      } else if (current >= total - 2) {
        buttons = [1, "...", total - 4, total - 3, total - 2, total - 1, total];
      } else {
        buttons = [1, "...", current - 1, current, current + 1, "...", total];
      }
    }

    return buttons;
  };

  // Fetch search results based on parameters
  const fetchResults = async (params) => {
    setLoading(true);

    try {
      let response;
      // Determine which API endpoint to use based on search type
      if (params.type === "standards") {
        response = await axios.get(BaseURL + "/api/search_standards/", {
          params,
        });
        setStandards(response.data.standards || []);
        setFundingAgencies([]);
        setInvestors([]);
        setPosts([]);

        // Update pagination
        const paginationData = response.data.pagination || {};
        setPagination({
          currentPage: paginationData.current_page || params.page,
          totalPages: paginationData.total_pages || 1,
          btnArray:
            paginationData.btnArray ||
            generatePaginationButtons(
              paginationData.current_page || params.page,
              paginationData.total_pages || 1
            ),
        });
      } else if (params.type === "funding_agencies") {
        // Use the same API endpoint but with the type parameter
        params.search = "Funding Agencies"; // Ensure the backend knows we're looking for funding agencies
        response = await axios.get(BaseURL + "/api/search_standards/", {
          params,
        });
        setStandards([]);
        setFundingAgencies(response.data.standards || []);
        setInvestors([]);
        setPosts([]);

        // Update pagination
        const paginationData = response.data.pagination || {};
        setPagination({
          currentPage: paginationData.current_page || params.page,
          totalPages: paginationData.total_pages || 1,
          btnArray:
            paginationData.btnArray ||
            generatePaginationButtons(
              paginationData.current_page || params.page,
              paginationData.total_pages || 1
            ),
        });
      } else if (
        params.type === "Investors" ||
        params.search?.toLowerCase().includes("investor")
      ) {
        response = await axios.get(BaseURL + "/api/search_investors/", {
          params,
        });
        setStandards([]);
        setFundingAgencies([]);
        setInvestors(response.data.investors || []);
        setPosts([]);

        // Update pagination
        const paginationData = response.data.pagination || {};
        setPagination({
          currentPage: paginationData.current_page || params.page,
          totalPages: paginationData.total_pages || 1,
          btnArray:
            paginationData.btnArray ||
            generatePaginationButtons(
              paginationData.current_page || params.page,
              paginationData.total_pages || 1
            ),
        });
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle service selection from Options component
  const handleServiceSelect = (service) => {
    handleSearchSubmit({ type: service, page: 1 });
  };

  return (
    <main className="bg-[#181828] pt-28 md:pt-36 pb-10">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-6 xl:px-0 w-full py-5 mb-5 md:mb-14">
      {/* Global search component */}
      <Global onSearch={handleSearchSubmit} />
      <br />

      {/* Internal search component */}
      <Internal onSearch={handleSearchSubmit} />

      {/* Recommendation Modal */}
      <RecommendationModal
        isOpen={isRecommendationModalOpen}
        onClose={() => setIsRecommendationModalOpen(false)}
        onYes={handleRecommendationYes}
      />

      {/* Display standards if available */}
      {standards && standards.length > 0 ? (
        <section className="py-10 mx-auto w-full max-w-[1280px] ">
          <div className="flex justify-between items-start">
            <div className="">
              <p className="text-xl md:text-[32px] font-semibold text-color max-w-xl leading-10">
                Explore Standards
              </p>
            </div>
          </div>

          <div className="mt-5">
            <div className="overflow-x-auto">
              <div className="border border-[#2d6bd8]">
                {standards.map((standard, index) => (
                  <div
                    key={index}
                    className="w-full flex flex-col md:flex-row border-b border-[#2d6bd8] lg:gap-3"
                  >
                    <div className="sm:border-r border-[#2d6bd8] flex text-white opecity-50 items-center p-2 sm:min-w-[200px] md:w-[200px] pr-2">
                      {standard.number}
                    </div>
                    <div className="flex gap-2 sm:flex-1">
                      <div className="p-2 text-white opecity-50">{standard.text}</div>
                    </div>
                    <div className="gap-2 flex items-center py-2 pr-2 justify-end flex-wrap sm:min-w-[200px]">
                      <div className="whitespace-nowrap px-4 main_btn text-white rounded-full p-1">
                        {standard.country}
                      </div>
                      <a
                        href={standard.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 main_btn text-white rounded-full p-1"
                      >
                        <i className="fa-solid fa-arrow-up-right-from-square"></i>{" "}
                        Visit
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Standards Pagination */}
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              btnArray={pagination.btnArray}
              onPageChange={handlePageChange}
            />
          </div>
        </section>
      ) : null}

      {/* Display funding agencies if available */}
      {fundingAgencies && fundingAgencies.length > 0 ? (
        <section className="py-10 mx-auto w-full max-w-[1280px] px-6 xl:px-0">
          <div className="flex justify-between items-start">
            <div className="">
              <p className="text-xl md:text-[32px] font-semibold text-color max-w-xl leading-10">
                Explore Funding Agencies
              </p>
            </div>
          </div>

          <div className="mt-5">
            <div className="">
              {fundingAgencies.map((agency, index) => (
                <div
                  key={index}
                  className={`flex flex-col md:flex-row border gap-3 p-3 md:items-center justify-between ${
                    index === 0 ? "border-t border-[#2d6bd8]" : "border-t-0 border-[#2d6bd8]"
                  }`}
                >
                  <div className="w-full text-white opecity-40">{agency.text}</div>
                  <div className="w-[100px]">
                    <a
                      href={agency.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 main_btn text-white rounded-full p-2"
                    >
                      <i className="fa-solid fa-arrow-up-right-from-square"></i>{" "}
                      Visit
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Funding Agencies Pagination */}
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              btnArray={pagination.btnArray}
              onPageChange={handlePageChange}
            />
          </div>
        </section>
      ) : searchParams.type === "Funding Agencies" && !loading ? (
        <section className="py-10 mx-auto w-full max-w-[1280px] px-6 xl:px-0">
          <div className="flex justify-between items-start">
            <div className="">
              <p className="text-xl md:text-[32px] font-semibold text-color max-w-xl leading-10">
                No Funding Agencies found
              </p>
            </div>
          </div>
        </section>
      ) : null}

      {/* Display investors if available */}
      {investors && investors.length > 0 ? (
        <div className="flex border border-[#2d6bd8] mt-8 rounded-[22px]">
          <div className="w-full">
            <InvestorContainer
              investors={investors}
              investorsPageData={pagination}
              handlePageChange={handlePageChange}
            />
          </div>
        </div>
      ) : investors &&
        investors.length === 0 &&
        searchParams.type === "Investors" &&
        !loading ? (
        <section className="py-10 mx-auto w-full max-w-[1280px] px-6 xl:px-0">
          <div className="flex justify-between items-start">
            <div className="">
              <p className="text-xl md:text-[32px] font-semibold text-color max-w-xl leading-10">
                No investors found
              </p>
            </div>
          </div>
        </section>
      ) : null}

      {/* Loading indicator */}
      {loading && (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#fbbb04]"></div>
        </div>
      )}
      </div>
    </main>
  );
}
